/**
 * 사주 저장 관리 컴포저블
 * 
 * 책임:
 * - 사주 데이터 저장
 * - 데이터 변환 및 포맷팅
 * - 그룹 관리
 * - 메모 저장
 */

import { userGroupSave } from '@/api/Eoway';
import { saveMemo, updateGroup } from '@/api/UserGroup';
import { DEV_MODE } from '@/config/dev-mode';

export function useSajuSave() {
  // ===== 내부 상태 (외부에서 주입받음) =====
  let _systemId = null;
  let _sajuData = null;
  let _transdata = null;
  let _solarDate = null;
  let _lunarDate = null;
  let _userGroupId = null;
  let _userGroupName = null;
  let _memoContent = null;
  let _pgmode = null;
  let _id = null;
  let _isPopSajuGroupChange = null;
  let _showDialogAlert = null;
  let _getParams = null;

  // ===== 초기화 =====
  
  /**
   * 컴포저블 초기화
   * @param {Object} deps - 외부 의존성
   */
  const initialize = (deps) => {
    _systemId = deps.systemId;
    _sajuData = deps.sajuData;
    _transdata = deps.transdata;
    _solarDate = deps.solarDate;
    _lunarDate = deps.lunarDate;
    _userGroupId = deps.userGroupId;
    _userGroupName = deps.userGroupName;
    _memoContent = deps.memoContent;
    _pgmode = deps.pgmode;
    _id = deps.id;
    _isPopSajuGroupChange = deps.isPopSajuGroupChange;
    _showDialogAlert = deps.showDialogAlert;
    _getParams = deps.getParams;
  };

  // ===== 저장 기능 =====
  
  /**
   * 사주 데이터 저장
   */
  const saveSajuData = async () => {
    if (!_sajuData?.value || Object.keys(_sajuData.value).length === 0) return;
    const convertedData = convertSajuData(_sajuData.value);

    try {
      const result = await userGroupSave(convertedData);
      await saveSajuMemo(result.guestUserId);
    } catch (e) {
      DEV_MODE && console.error('saveSajuData error:', e);
      _showDialogAlert('저장하기 오류');
    }
  };

  /**
   * 사주 저장 데이터 변환
   * @param {Object} sajuData - 사주 데이터
   * @returns {Object} 변환된 데이터
   */
  const convertSajuData = (sajuData) => {
    const { inputdata } = sajuData;

    let convertedData = {
      systemId: _systemId,
      description: '',
      userGroup: null,
      option1: inputdata.earlytime,
      option4: inputdata.koreantime,
      isLeapYear: inputdata.leapyear,
      blNum: _transdata?.value?.lucknum,
    };

    // 사용자 기본 정보 변환
    convertedData = convertAddGuestUserInfo(convertedData, sajuData);
    // 사용자 생년월일 정보 변환
    convertedData = convertAddGuestBirthInfo(convertedData, sajuData);
    // 사용자 사주간지 정보 변환
    convertedData = convertAddGuestBirthGanji(convertedData, sajuData);
    // 사용자 출생지역 정보 변환
    convertedData = convertAddGuestBornRegion(convertedData, sajuData);
    // 사용자 출생 절기 정보 변환
    convertedData = convertAddGuestBirthSeason(convertedData, sajuData);
    // 사용자 선택 그룹 정보 변환
    convertedData = convertAddGuestSelectedGroup(convertedData);

    return convertedData;
  };

  /**
   * 사용자 기본 정보 변환
   */
  const convertAddGuestUserInfo = (body, result) => {
    if (!result) return body;

    const { inputdata } = result;

    if (!inputdata) return body;
    return {
      ...body,
      guestUserName: inputdata.name,
      gender: inputdata.gender === '1' ? 'MEN' : 'WOMEN',
      isLuna: inputdata.lunsol === '음력',
    };
  };

  /**
   * 사용자 생년월일 정보 변환
   */
  const convertAddGuestBirthInfo = (body, result) => {
    let newBody = { ...body };
    if (!result) return newBody;

    const { inputdata } = result;

    if (inputdata) {
      // 백엔드 최상위 solar/lunar 사용
      const solar = _solarDate?.value;
      const lunar = _lunarDate?.value;
      const { lunsol, hour, min } = inputdata;
      newBody = { ...newBody, solar, lunar };

      const birth = lunsol === '음력' ? lunar : solar;
      const birthTime = `${hour < 10 ? '0' : ''}${hour}:${min < 10 ? '0' : ''}${min}`;

      newBody = {
        ...newBody,
        birth,
        birthTime,
      };
    }

    return newBody;
  };

  /**
   * 사용자 사주간지 정보 변환
   */
  const convertAddGuestBirthGanji = (body, result) => {
    if (!result) return body;

    const { birthganji } = result;

    if (!birthganji) return body;

    const { sky, earth } = birthganji;
    let sajugangi = '';
    let luck = '';
    sajugangi += `${sky.hour.value}${sky.day.value}${sky.month.value}${sky.year.value}`;
    sajugangi += `${earth.hour.value}${earth.day.value}${earth.month.value}${earth.year.value}`;
    luck = `${sky.luck.value}${earth.luck.value}`;

    return { ...body, sajugangi, luck };
  };

  /**
   * 사용자 출생지역 정보 변환
   */
  const convertAddGuestBornRegion = (body, result) => {
    const { inputdata, transdata } = result;
    if (!inputdata || !transdata) return body;
    if (!inputdata.loc) return body;
    return {
      ...body,
      bornAreaId: inputdata.loc,
      bornArea: transdata.bornArea.county,
    };
  };

  /**
   * 사용자 출생 절기 정보 변환
   */
  const convertAddGuestBirthSeason = (body, result) => {
    const { inputdata } = result;
    if (!inputdata) return body;
    if (inputdata.dongji) return { ...body, option2: true };
    if (inputdata.dongji === false) return { ...body, option2: false };
    return { ...body, option2: null };
  };

  /**
   * 사용자 선택 그룹 정보 변환
   */
  const convertAddGuestSelectedGroup = (body) => {
    if (_userGroupId?.value === -1 || _userGroupId?.value === null) return body;

    return { ...body, userGroup: { userGroupId: _userGroupId.value } };
  };

  /**
   * 저장 사주의 메모 등록
   * @param {number} guestUserId - 게스트 사용자 ID
   */
  const saveSajuMemo = async (guestUserId) => {
    if (!_memoContent?.value || !guestUserId) {
      _showDialogAlert('저장되었습니다.', '', '/saju-service');
      return;
    }

    try {
      await saveMemo({
        systemId: _systemId,
        content: _memoContent.value,
        guestUser: {
          guestUserId,
        },
      });
      _showDialogAlert('저장되었습니다.', '', '/saju-service');
    } catch (e) {
      DEV_MODE && console.error('saveSajuMemo error:', e);
      _showDialogAlert(e.response.data?.message);
    }
  };

  // ===== 그룹 관리 =====
  
  /**
   * 그룹 변경
   * @param {Array} selectedGroup - 선택된 그룹
   */
  const changeGroup = async (selectedGroup) => {
    _userGroupId.value = selectedGroup[0]?.userGroupId;
    _userGroupName.value = selectedGroup[0]?.name;
    
    if (_pgmode?.value === 'rs') {
      try {
        DEV_MODE && console.log('id.value: ', _id?.value);
        await updateGroup({
          guestUserId: _id.value,
          userGroupId: _userGroupId.value,
        });
      } catch (error) {
        _showDialogAlert(error.message || 'Error occurred while fetching details.');
      }
      await _getParams();
    }
    
    if (_isPopSajuGroupChange) {
      _isPopSajuGroupChange.value = false;
    }
  };

  // ===== 반환 =====
  
  return {
    // 메서드
    initialize,
    saveSajuData,
    convertSajuData,
    saveSajuMemo,
    changeGroup,
  };
}
